/**
 * LES SURVEILLANCES D'UN ÉQUIPEMENT (L'ONGLET « SURVEILLANCE » D'UNE SOURCE OU
 * D'UN BÂTIMENT) ET LEURS ALARMES OUVERTES.
 * ============================================================================
 *
 * INTENTION : sur la fiche d'une sonde ou d'un bâtiment, le client lit les
 * surveillances de cet équipement comme dans la liste, et en ouvre une.
 *
 * La lecture suit les règles de la liste (`surveillances.store.ts`) :
 *  • `GET /instance?objet_type=…&_id_objet=…`, le curseur suivi jusqu'à `null`,
 *    au plus `PLAFOND_PAGES_VIDES` pages vides de suite, puis « Afficher la
 *    suite » ; une page vide avec un curseur n'est pas une fin ;
 *  • les alarmes ouvertes par la liste (`ouverte=true`), jamais par le détail
 *    (le lire clôt une alarme revenue à la normale et non vue, D71) ; une
 *    alarme ouverte qu'on ne sait pas rattacher, ou un plafond atteint, fait
 *    échouer la lecture : jamais de faux calme ;
 *  • tenu par session (personne et entreprise) ET par équipement : toute fin
 *    de lecture qui n'est plus la courante est jetée sans toucher à l'état, en
 *    succès comme en échec. Jamais l'état d'une entreprise montré à une autre.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import {
    LIMITE_PAGE,
    PLAFOND_PAGES_VIDES,
    type StatutListe,
} from "@modules/alarmes-v2/stores/surveillances.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import {
    CODE_INDISPONIBLE,
    ErreurAlarmesV2,
    versErreurAlarmesV2,
} from "@modules/alarmes-v2/services/appel"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Le plafond de pages d'alarmes ouvertes, garde contre une boucle sans fin. */
const PLAFOND_PAGES_ALARMES = 50

/** L'équipement dont on lit les surveillances. */
export interface EquipementVise {
    objetType: "source" | "building"
    idObjet: string
}

/** Levée quand la session ou l'équipement a changé en cours de lecture. */
class LectureObsolete extends Error {}

/** Une réponse qu'on ne sait pas lire en entier : la lecture échoue. */
const illisible = () =>
    new ErreurAlarmesV2({ code: CODE_INDISPONIBLE, statut: null })

/** La clé d'une lecture : la session, puis l'équipement. */
const cleDe = (session: string, e: EquipementVise) =>
    session ? `${session}|${e.objetType}|${e.idObjet}` : ""

export const useEquipementStore = defineStore("alarmesV2Equipement", () => {
    /** Les surveillances de l'équipement, dans l'ordre du service. */
    const surveillances = ref<AlarmesV2Surveillance[]>([])

    /** L'alarme ouverte de chaque surveillance, par `_id_instance`. */
    const alarmesParInstance = ref<Record<string, AlarmesV2Alarme>>({})

    /** Les noms servis avec les surveillances et les alarmes. */
    const personnes = ref<Record<string, AlarmesV2Personne>>({})

    /** Où en est la lecture. */
    const statut = ref<StatutListe>("attente")

    /** L'échec de la dernière lecture, `null` sinon. */
    const erreur = ref<ErreurAlarmesV2 | null>(null)

    /** Vrai quand la lecture s'est arrêtée au plafond de pages vides. */
    const suiteDisponible = ref(false)

    /** La clé lue (session et équipement), `""` sinon. */
    const cleLue = ref("")

    let curseurSuite: string | null = null
    let equipementLu: EquipementVise | null = null
    let lectureCourante = 0
    let relectureCourante = 0

    function vider(): void {
        surveillances.value = []
        alarmesParInstance.value = {}
        personnes.value = {}
        erreur.value = null
        suiteDisponible.value = false
        curseurSuite = null
    }

    function verifier(cle: string, numero: number, e: EquipementVise): void {
        if (numero !== lectureCourante || cleDe(cleDeSession(), e) !== cle)
            throw new LectureObsolete()
    }

    async function lireLesSurveillances(
        cle: string,
        numero: number,
        e: EquipementVise,
        curseur: string | null
    ): Promise<void> {
        let suivant = curseur
        let videsDeSuite = 0
        do {
            const page = await serviceAlarmesV2.alarmesV2ListerSurveillances({
                objet_type: e.objetType,
                _id_objet: e.idObjet,
                limite: LIMITE_PAGE,
                ...(suivant ? { curseur: suivant } : {}),
            })
            verifier(cle, numero, e)
            if (!Array.isArray(page?.items)) throw illisible()
            surveillances.value = [...surveillances.value, ...page.items]
            personnes.value = { ...personnes.value, ...(page.personnes ?? {}) }
            videsDeSuite = page.items.length === 0 ? videsDeSuite + 1 : 0
            suivant = page.curseur_suivant ?? null
        } while (suivant && videsDeSuite < PLAFOND_PAGES_VIDES)
        curseurSuite = suivant
        suiteDisponible.value = suivant !== null
    }

    async function lireLesAlarmesOuvertes(
        cle: string,
        numero: number,
        e: EquipementVise
    ): Promise<void> {
        const lu = await lireLesAlarmes(() => verifier(cle, numero, e))
        alarmesParInstance.value = lu.parInstance
        personnes.value = { ...personnes.value, ...lu.noms }
    }

    /** Lit les alarmes ouvertes sans rien poser ; `verif` lève si la lecture est dépassée. */
    async function lireLesAlarmes(verif: () => void): Promise<{
        parInstance: Record<string, AlarmesV2Alarme>
        noms: Record<string, AlarmesV2Personne>
    }> {
        const parInstance: Record<string, AlarmesV2Alarme> = {}
        const noms: Record<string, AlarmesV2Personne> = {}
        let suivant: string | null = null
        let pages = 0
        do {
            const page = await serviceAlarmesV2.alarmesV2ListerAlarmes({
                ouverte: true,
                limite: LIMITE_PAGE,
                ...(suivant ? { curseur: suivant } : {}),
            })
            verif()
            if (!Array.isArray(page?.items)) throw illisible()
            for (const alarme of page.items) {
                if (
                    typeof alarme?._id_instance !== "string" ||
                    alarme._id_instance === ""
                )
                    throw illisible()
                if (!parInstance[alarme._id_instance])
                    parInstance[alarme._id_instance] = alarme
            }
            Object.assign(noms, page.personnes ?? {})
            suivant = page.curseur_suivant ?? null
            pages += 1
        } while (suivant && pages < PLAFOND_PAGES_ALARMES)
        if (suivant) throw illisible()
        return { parInstance, noms }
    }

    async function lancer(
        cle: string,
        e: EquipementVise,
        lecture: (numero: number) => Promise<void>
    ): Promise<void> {
        lectureCourante += 1
        const numero = lectureCourante
        statut.value = "chargement"
        try {
            await lecture(numero)
            if (numero !== lectureCourante || cleDe(cleDeSession(), e) !== cle)
                return
            statut.value = "pret"
        } catch (err) {
            if (numero !== lectureCourante || cleDe(cleDeSession(), e) !== cle)
                return
            vider()
            erreur.value =
                err instanceof ErreurAlarmesV2 ? err : versErreurAlarmesV2(err)
            statut.value = "erreur"
        }
        cleLue.value = cle
    }

    /**
     * Lit les surveillances de l'équipement, toujours relues (entrée sur
     * l'onglet). Ne rejette jamais.
     */
    async function charger(e: EquipementVise): Promise<void> {
        const cle = cleDe(cleDeSession(), e)
        if (!cle || !e.idObjet) {
            reinitialiser()
            return
        }
        vider()
        equipementLu = { ...e }
        return lancer(cle, e, async (numero) => {
            await lireLesSurveillances(cle, numero, e, null)
            await lireLesAlarmesOuvertes(cle, numero, e)
        })
    }

    /** Reprend la lecture après le plafond de pages vides. */
    async function afficherLaSuite(): Promise<void> {
        const e = equipementLu
        if (!e || !curseurSuite) return
        const cle = cleDe(cleDeSession(), e)
        if (cle !== cleLue.value) return
        const depuis = curseurSuite
        suiteDisponible.value = false
        return lancer(cle, e, async (numero) => {
            await lireLesSurveillances(cle, numero, e, depuis)
            await lireLesAlarmesOuvertes(cle, numero, e)
        })
    }

    /**
     * Relit les alarmes ouvertes de l'équipement déjà lu, après un
     * acquittement, SANS vider la liste : les surveillances et « Afficher la
     * suite » restent ; les alarmes sont remplacées d'un coup quand la
     * relecture a abouti. Un échec ne touche à rien et rend faux.
     */
    async function relireLesAlarmes(): Promise<boolean> {
        const e = equipementLu
        if (!e || statut.value !== "pret") return false
        const cle = cleDe(cleDeSession(), e)
        if (!cle || cle !== cleLue.value) return false
        const numero = lectureCourante
        relectureCourante += 1
        const relecture = relectureCourante
        const encore = () =>
            numero === lectureCourante &&
            relecture === relectureCourante &&
            cleDe(cleDeSession(), e) === cle &&
            statut.value === "pret"
        try {
            const lu = await lireLesAlarmes(() => {
                if (!encore()) throw new LectureObsolete()
            })
            if (!encore()) return false
            alarmesParInstance.value = lu.parInstance
            personnes.value = { ...personnes.value, ...lu.noms }
            return true
        } catch {
            return false
        }
    }

    /** L'alarme ouverte d'une surveillance, ou `null`. */
    function alarmeDe(idSurveillance: string): AlarmesV2Alarme | null {
        return alarmesParInstance.value[idSurveillance] ?? null
    }

    /** Remet le store à son état de départ ; toute lecture en vol est jetée. */
    function reinitialiser(): void {
        vider()
        lectureCourante += 1
        statut.value = "attente"
        cleLue.value = ""
        equipementLu = null
    }

    return {
        surveillances,
        personnes,
        statut,
        erreur,
        suiteDisponible,
        cleLue,
        charger,
        afficherLaSuite,
        relireLesAlarmes,
        alarmeDe,
        reinitialiser,
    }
})
